import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme, THEME_LABELS, THEME_DESCRIPTIONS, type Theme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Z_INDEX, zStyle } from '../../lib/zLayers';
import api from '../../services/api';

/** Mini UI preview: sidebar strip + surface + accent chip */
const THEME_PREVIEW: Record<
  Theme,
  { shell: string; panel: string; accent: string; dot: string }
> = {
  light: {
    shell: 'bg-[#0a1424]',
    panel: 'bg-slate-100',
    accent: 'bg-[#6b8fb8]',
    dot: 'bg-[#3d5a80]',
  },
  dark: {
    shell: 'bg-[#060a14]',
    panel: 'bg-[#121a2b]',
    accent: 'bg-[#6b8fb8]',
    dot: 'bg-[#6b8fb8]',
  },
  ocean: {
    shell: 'bg-[#0a1628]',
    panel: 'bg-sky-50',
    accent: 'bg-sky-500',
    dot: 'bg-sky-500',
  },
  harbor: {
    shell: 'bg-[#042022]',
    panel: 'bg-teal-50',
    accent: 'bg-teal-500',
    dot: 'bg-teal-500',
  },
  slate: {
    shell: 'bg-zinc-950',
    panel: 'bg-zinc-100',
    accent: 'bg-zinc-600',
    dot: 'bg-zinc-600',
  },
};

function ThemePreview({ themeId, selected }: { themeId: Theme; selected: boolean }) {
  const p = THEME_PREVIEW[themeId];
  return (
    <span
      className={cn(
        'relative flex h-10 w-14 shrink-0 overflow-hidden rounded-lg border shadow-sm',
        selected ? 'border-current ring-2 ring-current/25' : 'border-black/10 dark:border-white/15',
      )}
      aria-hidden
    >
      <span className={cn('w-[34%] h-full', p.shell)} />
      <span className={cn('relative flex-1 h-full', p.panel)}>
        <span className={cn('absolute right-1.5 top-1.5 h-2 w-2 rounded-full', p.accent)} />
        <span className={cn('absolute left-1.5 bottom-2 h-1 w-5 rounded-full opacity-40', p.accent)} />
        <span className={cn('absolute left-1.5 bottom-4 h-1 w-3.5 rounded-full opacity-25', p.accent)} />
      </span>
    </span>
  );
}

const ThemeSwitcher: React.FC = () => {
  const { theme, setTheme, availableThemes, colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPos(null);
      return;
    }
    updatePosition();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    const handleReposition = () => updatePosition();

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={cn(
          'p-2 rounded-lg transition-colors focus:outline-none focus:ring-2',
          colors.textMuted,
          colors.interactiveHover,
          isOpen && cn(colors.surfaceHover, colors.textSecondary),
        )}
        title={`Theme: ${THEME_LABELS[theme]}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {/* Palette — appearance / theme */}
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 21a9 9 0 119-9c0 1.657-1.343 2.5-3 2.5h-1.5a1.5 1.5 0 00-1.5 1.5c0 1.657-1.343 3-3 3z"
          />
          <circle cx="7.5" cy="11" r="1" fill="currentColor" stroke="none" />
          <circle cx="10.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
          <circle cx="14.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
          <circle cx="17" cy="11" r="1" fill="currentColor" stroke="none" />
        </svg>
      </button>

      {isOpen &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className={cn(
              'fixed w-[17.5rem] rounded-xl border shadow-xl py-2',
              colors.surface,
              colors.border,
            )}
            style={{
              top: menuPos.top,
              right: menuPos.right,
              ...zStyle(Z_INDEX.portaledDropdown),
            }}
          >
            <div className={cn('px-3.5 pb-2 pt-1 border-b mb-1', colors.border)}>
              <p className={cn('text-[11px] font-semibold uppercase tracking-[0.14em]', colors.textMuted)}>
                Appearance
              </p>
              <p className={cn('mt-0.5 text-xs', colors.textSecondary)}>
                Choose how ATLAS looks for you
              </p>
            </div>

            <div className="px-1.5 py-1 space-y-0.5">
              {availableThemes.map((themeOption) => {
                const selected = theme === themeOption;
                return (
                  <button
                    key={themeOption}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    onClick={() => {
                      setTheme(themeOption);
                      setIsOpen(false);
                      void api.patch('/settings/user', { theme: themeOption }).catch(() => undefined);
                    }}
                    className={cn(
                      'w-full flex items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors',
                      selected
                        ? cn(colors.surfaceHover, 'ring-1', colors.border)
                        : colors.interactiveHover,
                    )}
                  >
                    <ThemePreview themeId={themeOption} selected={selected} />
                    <span className="min-w-0 flex-1">
                      <span className={cn('block text-sm font-medium leading-tight', colors.text)}>
                        {THEME_LABELS[themeOption]}
                      </span>
                      <span className={cn('mt-0.5 block text-[11px] leading-snug', colors.textMuted)}>
                        {THEME_DESCRIPTIONS[themeOption]}
                      </span>
                    </span>
                    <span
                      className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                        selected
                          ? cn(colors.primary, colors.primaryText, 'border-transparent')
                          : cn(colors.border, 'bg-transparent'),
                      )}
                      aria-hidden
                    >
                      {selected && (
                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default ThemeSwitcher;
